import { useEffect, useSyncExternalStore } from "react";

import type { ModuleKey } from "@/lib/module-registry";

/**
 * The active module — nav context, not a property of the URL.
 *
 * **Why this exists.** Shared records (clients, accounts, suppliers,
 * utilities) have one canonical module-neutral url each, so the url alone can't
 * say which module's chrome should wrap them. Previously the record's own route
 * carried a `globalShell` flag, which meant drilling into a client from Energy Advisor
 * silently swapped you into the Global sidebar — an involuntary module switch on
 * every drill-in. Salesforce and Dynamics both avoid this the same way: the
 * record is module-independent and the module you're in persists across the
 * drill-in.
 *
 * **How it resolves**, in order:
 *  1. The matched route's own `moduleKey`, when it has one — a module's own pages
 *     are unambiguous, and navigating to one *is* a module switch.
 *  2. Otherwise (a shared record route), this sticky value — the last module
 *     you were actually in.
 *  3. Otherwise the default module, for a cold deep link straight to a record
 *     with no prior context (a shared link, a bookmark, a new tab).
 *
 * Persisted per browser so a reload inside a module stays in that module.
 * Session-scoped rather than server-side on purpose: it's a UI preference,
 * not authorization — every route keeps its own permission/feature gates
 * regardless of which chrome renders it.
 */

// Deliberately still says `app` after the apps -> modules rename: this is a
// persisted key in every existing user's localStorage. Renaming it would read
// as a miss on the first load after deploy and silently reset everyone's
// sticky module to the default. The concept is "module"; the key is history.
const STORAGE_KEY = "powerup:active-app";

/** Where a cold deep link to a shared record lands when there's no context. */
export const DEFAULT_MODULE_KEY: ModuleKey = "energy-advisor";

const listeners = new Set<() => void>();

/**
 * Module keys retired by a rename, mapped to their replacement.
 *
 * The stored value is a key from whenever the user last navigated, so a
 * rename leaves stale keys in real browsers. An unmapped stale key is handled
 * — `moduleOrDefault` falls back to the default module — but it silently
 * drops the user's actual last module. Mapping the rename keeps stickiness
 * across the deploy. Safe to delete an entry once the deploy is old enough
 * that no live browser could still hold it.
 */
const RENAMED_KEYS: Record<string, ModuleKey> = {
  // "Sales" became "Energy Advisor" when its back-office pages split out into
  // the Operations module.
  sales: "energy-advisor",
};

/** The stored value, unmapped. `null` when absent or storage is blocked. */
function readRaw(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage — fall back rather than throw. Losing
    // stickiness is a much smaller failure than a shell that won't render.
    return null;
  }
}

function read(): ModuleKey {
  const raw = readRaw();
  if (raw === null) return DEFAULT_MODULE_KEY;
  return RENAMED_KEYS[raw] ?? (raw as ModuleKey);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * Remembers `key` as the module the user is in. Called by `AppShell` whenever a
 * route carrying its own `moduleKey` renders — so entering a module sticks, and a
 * later drill-in to a shared record inherits it.
 */
export function setActiveModule(key: ModuleKey): void {
  // Compare the RAW stored value, not `read()`'s mapped one. `read()` resolves
  // a retired key through `RENAMED_KEYS`, so comparing against it made the
  // migration never persist: with `"sales"` in storage, `read()` returned
  // `"energy-advisor"`, this early return fired, and `"sales"` stayed in that
  // browser forever. That left the cleanup note on `RENAMED_KEYS` unreachable
  // — deleting the entry later would have reset those users to the default
  // module, the exact outcome the mapping exists to prevent.
  if (readRaw() === key) return;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* ignore — see `read` */
    }
  }
  for (const l of listeners) l();
}

/** Reactive read of the sticky active module. */
export function useStickyActiveModule(): ModuleKey {
  return useSyncExternalStore(subscribe, read, () => DEFAULT_MODULE_KEY);
}

/** Non-reactive read, for callers outside React. */
export function getStickyActiveModule(): ModuleKey {
  return read();
}

/**
 * Resolves the module whose chrome should render, given the matched route's own
 * `moduleKey` (if any). Falls back to the sticky value, then the default.
 *
 * The route's own key wins immediately (returned this render, not after an
 * effect) so entering a module never paints one frame of the previous module's
 * sidebar. Persisting it is the side effect, so it runs in an effect rather
 * than during render — writing to the external store mid-render would notify
 * subscribers while React is still rendering.
 */
export function useResolvedModuleKey(routeModuleKey: string | undefined): ModuleKey {
  const sticky = useStickyActiveModule();
  const resolved = (routeModuleKey as ModuleKey | undefined) ?? sticky;

  useEffect(() => {
    // Only a module's OWN page updates the sticky value. A shared record must
    // not overwrite it — that's what keeps a drill-in from switching modules.
    if (routeModuleKey) setActiveModule(routeModuleKey as ModuleKey);
  }, [routeModuleKey]);

  return resolved;
}
